import React, { useEffect, useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import PageLayout from "../../_layout/PageLayout";
import {
  ManageDeletedData,
  ManageReportTemplates,
} from "../../../services/authorization/Permissions";
import { api } from "../../../services/api/ApiService";
import { SettingsMenuItemsModel } from "../SettingsMenuItems";
import { ReportTemplatesStorageModel } from "../../../services/organization/ReportTemplatesStorage";
import DataGrid from "../../../components/datatable/DataGrid";
import {
  ActionsSection,
  ButtonsSection,
} from "../../../components/data-components/sections/DataSections";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import UploadReportTemplate from "./components/buttons/UploadReportTemplateButton";
import { DownloadReportTemplateButton } from "./components/buttons/DownloadReportTemplateButton";
import { DownloadDefaultReportTemplateButton } from "./components/buttons/DownloadDefaultReportTemplateButton";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import {
  ApplicationError,
  errorHandler,
} from "../../../services/error-handler/ErrorHandlerService";
import { ReportTemplateResponse } from "../../../api-models/response/organization/ReportTemplateResponse";

export default observer(() => {
  const menuItems = useMemo(() => {
    const settingsMenuItems = SettingsMenuItemsModel.create({});

    return {
      staticItems: settingsMenuItems.getStaticItems(),
    };
  }, []);

  const [reportTemplatesStorage] = useState(() =>
    ReportTemplatesStorageModel.create({ useGridFiltering: true }, { api })
  );

  const isDeleteDisabled = useMemo(
    () =>
      !reportTemplatesStorage.anyItemsSelected ||
      !permissionsService.userHasPermissionTo(ManageReportTemplates),
    [reportTemplatesStorage.anyItemsSelected]
  );

  const isAnySelectedReportTemplateUsedByAProject = useCallback(() => {
    const selectedReportTemplateIds: string[] = reportTemplatesStorage.selectedItems;
    if (!selectedReportTemplateIds.length) return false;

    const selectedReportTemplatesUsedByProjects = reportTemplatesStorage.data?.items.filter(
      (x: ReportTemplateResponse) =>
        selectedReportTemplateIds.includes(x.id.toString()) && x.projectsCount > 0
    );
    return selectedReportTemplatesUsedByProjects.length > 0;
  }, [reportTemplatesStorage.data?.items, reportTemplatesStorage.selectedItems]);

  const deleteSelectedFiles = useCallback(async () => {
    const selectedItemsCount = reportTemplatesStorage.selectedItemsCount;
    try {
      await reportTemplatesStorage.deleteReportTemplates(reportTemplatesStorage.selectedItems);
      snackbarNotificationsService.showSuccess("ReportTemplates.delete.success", {
        itemsCount: selectedItemsCount,
      });
    } catch (error) {
      const errorContext = { itemsCount: selectedItemsCount };
      const applicationError = error as ApplicationError;
      applicationError.withContext(errorContext);
      applicationError.withErrorMessage("default-error.report-templates.delete");
      errorHandler.handleErrorInSnackbar(applicationError);
    }
  }, [reportTemplatesStorage]);

  useEffect(() => {
    async function getFormats() {
      await reportTemplatesStorage.getProjectFormats();
    }
    getFormats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ManageReportTemplates]}
      id="view-report-templates"
      titleKey="ReportTemplates.title"
      menuItems={menuItems}
      isLoading={
        reportTemplatesStorage.isUploadInProgress || reportTemplatesStorage.areProjectFormatsLoading
      }
    >
      <ActionsSection>
        <ButtonsSection>
          <UploadReportTemplate reportTemplatesStorage={reportTemplatesStorage} />
          <DownloadReportTemplateButton reportTemplatesStorage={reportTemplatesStorage} />
          <DeleteButton
            disabled={isDeleteDisabled}
            confirmationAction={deleteSelectedFiles}
            confirmationModalTitleKey="ReportTemplates.delete.confirmation.title"
            confirmationModalTitleContext={{
              reportTemplatesCount: reportTemplatesStorage.selectedItemsCount,
            }}
            confirmationModalSubtitleKey={
              isAnySelectedReportTemplateUsedByAProject()
                ? "ReportTemplates.delete.confirmation.subtitle2"
                : "ReportTemplates.delete.confirmation.subtitle1"
            }
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
        <ButtonsSection>
          <DownloadDefaultReportTemplateButton reportTemplatesStorage={reportTemplatesStorage} />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.organization.report-templates"
        dataGridStorage={reportTemplatesStorage}
        noResultsMessageKey="ReportTemplates.empty-grid"
      />
    </PageLayout>
  );
});
